// Termin-bearbeiten-Dialog — extrahiert aus AppointmentsPage.js.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { CustomerSearchSelect } from '@/components/ui/customer-search-select';
import { useStudio } from '@/contexts/StudioContext';
import { BookingSourceIcon } from './calendarUtils';

export const EditAppointmentDialog = ({
  open, onOpenChange, appointment, services, customers,
  customTime, setCustomTime, bookingData, setBookingData, onSubmit,
}) => {
  const { studios } = useStudio();
  // Duo-Preishinweis (06/2026): Preis pro Teilnehmer bei Mehrpersonen-Terminen
  const [participantPrices, setParticipantPrices] = useState(null);
  const _ids = (appointment?.customer_ids || []).filter(Boolean);
  const _allIds = appointment?.customer_id && !_ids.includes(appointment.customer_id)
    ? [appointment.customer_id, ..._ids] : _ids;
  const isDuo = _allIds.length > 1;
  useEffect(() => {
    setParticipantPrices(null);
    if (!open || !appointment?.id || !isDuo) return;
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    const token = localStorage.getItem('token');
    axios.get(`${API}/appointments/${appointment.id}/participant-prices`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setParticipantPrices(r.data.participants || null))
      .catch(() => setParticipantPrices(null));
  }, [open, appointment?.id, isDuo]);
  const pricesDiffer = participantPrices &&
    new Set(participantPrices.map((p) => p.price)).size > 1;

  return (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          Termin bearbeiten
          {appointment?.is_addon && (
            <span className="text-xs font-normal px-2 py-1 rounded-full bg-purple-100 text-purple-700 flex items-center gap-1">
              + Zusatzleistung
            </span>
          )}
          {appointment?.booking_source && appointment.booking_source !== 'web' && (
            <span className="text-xs font-normal px-2 py-1 rounded-full bg-gray-100 flex items-center gap-1">
              <BookingSourceIcon source={appointment.booking_source} />
              {appointment.booking_source === 'chatbot' ? 'WhatsApp' :
               appointment.booking_source === 'app' ? 'App' :
               appointment.booking_source.startsWith('trainer:') ? appointment.booking_source.replace('trainer:', '') :
               appointment.booking_source}
            </span>
          )}
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        {/* Abrechnungs-Sperre: abgerechnete Termine sind read-only (ausser Notizen) */}
        {(appointment?.billing_status === 'billed' ||
          appointment?.billing_status === 'paid' ||
          appointment?.invoice_id) && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm" data-testid="edit-billed-lock-banner">
            <span className="font-medium text-amber-700">🔒 Abgerechnet</span>
            <span className="text-amber-600">
              {' '}— Zeit, Service, Kunde & Trainer können nicht mehr geändert werden (nur Notizen).
              Beim Stornieren wird eine bezahlte Rechnung automatisch als Guthaben gutgeschrieben.
            </span>
          </div>
        )}
        {/* Add-on info banner */}
        {appointment?.is_addon && appointment?.parent_appointment_id && (
          <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg text-sm">
            <span className="font-medium text-purple-700">Zusatzleistung</span>
            <span className="text-purple-600"> zu Haupttermin</span>
          </div>
        )}

        {/* Moved from info banner - separat von Notizen */}
        {appointment?.moved_from && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium text-blue-700">📦 Verschoben</span>
            </div>
            <div className="text-blue-600 text-xs space-y-0.5">
              <div>
                <span className="font-medium">Von:</span>{' '}
                {appointment.moved_from.original_start_time
                  ? new Date(appointment.moved_from.original_start_time).toLocaleString('de-DE', {
                      weekday: 'short',
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })
                  : 'Unbekannt'}
              </div>
              <div>
                <span className="font-medium">Verschoben am:</span>{' '}
                {appointment.moved_from.moved_at
                  ? new Date(appointment.moved_from.moved_at).toLocaleString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })
                  : 'Unbekannt'}
              </div>
            </div>
          </div>
        )}

        {/* EMS Anzugsgrösse Banner - nur bei EMS/Flex */}
        {/* Duo-Preishinweis: beide Teilnehmer mit ihrem jeweiligen Preis */}
        {isDuo && participantPrices && participantPrices.length > 1 && (
          <div
            className={`p-3 rounded-lg text-sm border ${pricesDiffer ? 'bg-orange-50 border-orange-200' : 'bg-emerald-50 border-emerald-200'}`}
            data-testid="duo-price-hint"
          >
            <div className={`font-medium mb-2 ${pricesDiffer ? 'text-orange-700' : 'text-emerald-700'}`}>
              👥 Preise pro Teilnehmer{pricesDiffer ? ' — unterschiedlich!' : ''}
            </div>
            <div className="space-y-1">
              {participantPrices.map((p) => (
                <div key={p.customer_id} className="flex items-center justify-between text-xs" data-testid={`duo-price-${p.customer_id}`}>
                  <span className={pricesDiffer ? 'text-orange-700' : 'text-emerald-700'}>{p.name}</span>
                  <span className="flex items-center gap-2">
                    <span className="text-gray-500">{p.source}</span>
                    <span className={`font-bold px-2 py-0.5 rounded text-white ${pricesDiffer ? 'bg-orange-500' : 'bg-emerald-600'}`}>
                      CHF {p.price.toFixed(2)}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {(() => {
          const service = services.find(s => s.id === appointment?.service_id);
          const isEmsOrFlex = service?.name?.toLowerCase().includes('ems') ||
                             service?.name?.toLowerCase().includes('flex') ||
                             appointment?.service_name?.toLowerCase().includes('ems') ||
                             appointment?.service_name?.toLowerCase().includes('flex');

          if (!isEmsOrFlex) return null;

          const suitSizes = appointment?.customer_suit_sizes || [];
          const customerNames = appointment?.customer_names || [];

          return (
            <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg text-sm">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-medium text-teal-700">👕 EMS Anzugsgrössen</span>
              </div>
              <div className="space-y-1">
                {customerNames.map((name, idx) => (
                  <div key={idx} className="flex items-center justify-between text-teal-700 text-xs">
                    <span>{name}:</span>
                    {suitSizes[idx] ? (
                      <span className="font-bold bg-teal-600 text-white px-2 py-0.5 rounded">{suitSizes[idx]}</span>
                    ) : (
                      <span className="text-orange-500">Nicht hinterlegt</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        <div className="space-y-2">
          <Label htmlFor="edit-time">Startzeit (anpassbar)</Label>
          <Input
            id="edit-time"
            type="time"
            value={customTime}
            onChange={(e) => setCustomTime(e.target.value)}
            placeholder="HH:MM"
          />
          <p className="text-xs text-gray-500">
            Sie können eine beliebige Zeit eingeben (z.B. 06:15, 06:45)
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="edit-duration">Dauer (Minuten)</Label>
          <Input
            id="edit-duration"
            data-testid="edit-duration-input"
            type="number"
            min="5"
            max="480"
            step="5"
            value={bookingData.duration_override ?? ''}
            onChange={(e) => setBookingData({ ...bookingData, duration_override: e.target.value })}
            placeholder={String(services.find((s) => s.id === bookingData.service_id)?.duration_minutes || '')}
          />
          <p className="text-xs text-gray-500">
            Leer lassen = Service-Standard ({services.find((s) => s.id === bookingData.service_id)?.duration_minutes || '–'} Min)
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="edit-service">Service</Label>
          <Select
            value={bookingData.service_id}
            onValueChange={(value) => setBookingData({ ...bookingData, service_id: value })}
          >
            <SelectTrigger id="edit-service">
              <SelectValue placeholder="Service wählen..." />
            </SelectTrigger>
            <SelectContent position="popper" sideOffset={5}>
              {services.map((service) => (
                <SelectItem key={service.id} value={service.id}>
                  <div className="flex justify-between items-center w-full">
                    <span>{service.name}</span>
                    <span className="ml-4 text-xs text-gray-500">
                      {service.duration_minutes} Min | {service.points_cost || 0} Pkt
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {studios && studios.length > 1 && (
          <div className="space-y-2">
            <Label htmlFor="edit-studio">Studio / Standort</Label>
            <Select
              value={bookingData.studio_id ?? appointment?.studio_id ?? ''}
              onValueChange={(value) => setBookingData({ ...bookingData, studio_id: value })}
            >
              <SelectTrigger id="edit-studio" data-testid="edit-studio-select">
                <SelectValue placeholder="Studio wählen..." />
              </SelectTrigger>
              <SelectContent position="popper" sideOffset={5}>
                {studios.map((st) => (
                  <SelectItem key={st.id} value={st.id} data-testid={`edit-studio-option-${st.id}`}>
                    {st.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">
              Korrigiert die Studio-Zuordnung dieses Termins (z.B. falsch auf Wetzikon statt Rapperswil gebucht).
            </p>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="edit-customer">Kunde</Label>
          <CustomerSearchSelect
            customers={customers}
            value={bookingData.customer_id}
            onValueChange={(value) => setBookingData({ ...bookingData, customer_id: value })}
            placeholder="Kunde suchen..."
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="edit-notes">Notizen</Label>
          <Textarea
            id="edit-notes"
            value={bookingData.notes}
            onChange={(e) => setBookingData({ ...bookingData, notes: e.target.value })}
            rows={3}
          />
        </div>

        <div className="flex gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1"
          >
            Abbrechen
          </Button>
          <Button
            onClick={onSubmit}
            className="flex-1"
            data-testid="edit-appointment-submit-btn"
          >
            Aktualisieren
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
  );
};
